'use client';

/**
 * CardErrorBoundary — contains a throwing card.
 *
 * Cards are mounted dynamically by content_key (Canvas2Card children,
 * PanelMount). Without a boundary, one card that throws during render
 * unmounts the whole subtree — "the viewport went blank". This wraps
 * each mount so a bad card shows a small inline fallback and the rest
 * of the canvas keeps working. Logs the throw so it gets fixed rather
 * than silently swallowed.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  /** content_key of the card being mounted — shown in the fallback + log. */
  contentKey?: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class CardErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[CardErrorBoundary] card "${this.props.contentKey ?? '?'}" threw:`, error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="border border-dashed border-destructive/40 bg-destructive/5 p-4 text-xs text-destructive">
          Card failed to render: <code className="font-mono">{this.props.contentKey ?? 'unknown'}</code>
          <div className="text-[11px] text-muted-foreground mt-1 break-words">{this.state.error.message}</div>
        </div>
      );
    }
    return this.props.children;
  }
}
